import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { NgForm, UntypedFormControl } from '@angular/forms';
import { SensitivityItem } from '../../../models/results/sensitivity/sensitivity-item.model';
import { Observable, Subject } from 'rxjs';
import { Sensitivity } from '../../../models/results/sensitivity/sensitivity.model';
import { InputSelectionTableItem } from '../../../models/data-structures/input-selection-table-item.model';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ProjectService } from '../../../project/project.service';
import { DeactivationDialogService } from '../../../helper/can-deactivate/deactivation-dialog.service';
import Utils from '../../../util';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import ResultsUtils from '../../results-util';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { LooseObject } from '../../../models/loose-object.model';
import { ProjectInputItem } from '../../../models/data-structures/project/project-input-item.model';

@Component({
  selector: 'app-input-selection',
  templateUrl: './sensitivity-input-selection.component.html',
  styleUrls: ['./sensitivity-input-selection.component.scss']
})
export class SensitivityInputSelectionComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  @ViewChild('inputsCheckbox', {static: true}) inputsCheckbox?: MatCheckbox;
  @ViewChild('sensTable') sensTable?: ElementRef<HTMLDivElement>;
  @ViewChild('sensForm') sensForm?: NgForm;
  inputsDropdownData: SensitivityItem[] = [];
  filteredInputsData: SensitivityItem[] = [];
  selectedInputData: any[] = [];
  sensitivity?: Sensitivity;
  changePercent = {name: 'changePercent', value: null, errors: []};
  showTable = false;
  displaySavePopup = false;
  displayResetPopup = false;
  sensTableData: InputSelectionTableItem[] = [];
  sensTableHeaders = ['include', 'variables', 'minimum', 'maximum', 'value', 'unit'];
  sensTableRowsSelected: Record<string, boolean> = {};
  selectedCount = 0;
  allRowsSelected = false;

  temperror = '';
  errorsSummaryDict = Object.freeze({
    'no-inputs': 'SENSITIVITY.ERRORS.ACTUAL.NO_INPUTS',
    'inconsistent-min-max': 'SENSITIVITY.ERRORS.ACTUAL.INCONSISTENT_MIN_MAX',
    'empty-input': 'SENSITIVITY.ERRORS.ACTUAL.EMPTY_INPUT',
    'negative-input': 'SENSITIVITY.ERRORS.ACTUAL.NEGATIVE_INPUT',
    'is-Nan': 'SENSITIVITY.ERRORS.ACTUAL.IS_NAN',
  });
  errorsHintsDict = Object.freeze({
    'inconsistent-min-max': 'SENSITIVITY.ERRORS.HINT.INCONSISTENT_MIN_MAX',
    'empty-input': 'SENSITIVITY.ERRORS.HINT.EMPTY_INPUT',
    'negative-input': 'SENSITIVITY.ERRORS.HINT.NEGATIVE_INPUT',
    'is-Nan': 'SENSITIVITY.ERRORS.HINT.IS_NAN',
    'non-positive': 'SENSITIVITY.ERRORS.HINT.NON_POSITIVE',
    'percent-over-100': 'SENSITIVITY.ERRORS.HINT.PERCENT_OVER_100'
  });
  errorsToCheck = {
    'no-inputs': false,
    'empty-input': false,
    'empty-apply-to-all': false,
    'inconsistent-min-max': true,
    'negative-input': true
  };
  formErrors: string[] = [];
  inputFilter = '';
  pageIsDirty = false;
  onDestroyNotifier = new Subject<boolean>();
  private projectLoaded = false;
  private unitsLoaded = false;
  trigger?: string;
  inputTranslations: LooseObject = {};


  constructor(
    private projCommService: ProjectCommunicationService,
    private projectService: ProjectService,
    private dialogService: DeactivationDialogService,
    private unitService: UnitService,
    private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit() {
    this.projCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
          this.projectLoaded = loaded;
          this.setup();
        }
      );
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitsLoaded = loaded;
        this.setup();
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.updateInputTranslations();
        this.trigger = language;
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.next(true);
    this.onDestroyNotifier.unsubscribe();
  }

  setup() {
    if (this.projectLoaded && this.unitsLoaded) {
      this.sensitivity = this.projCommService.getSensitivity();
      this.getProjectInputs();
      this.createTableData();
    }
  }

  filterInputs() {
    if (!this.inputsDropdownData) {
      return;
    }
    let search = this.inputFilter;
    if (!search) {
      this.filteredInputsData = this.inputsDropdownData.slice();
      this.setSelectedInputs();
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredInputsData = this.inputsDropdownData.filter(input => this.inputTranslations[input.id].toLowerCase().indexOf(search) > -1);

    // Add already selected inputs
    for (const input of this.selectedInputData) {
      // Ignore duplicate items that are in the filtered and selected lists
      if (!this.filteredInputsData.find(i => i === input)) {
        this.filteredInputsData.push(input);
      }
    }

    this.setSelectedInputs();
  }

  /**
   * updates the table when new inputs are selected
   */
  setSelectedInputs() {
    if (this.selectedInputData.length === 0) {
      this.addFormError('no-inputs');
    } else {
      this.removeFormError('no-inputs');
    }
    this.pageIsDirty = true;
    this.createTableData();
  }

  rowsCheckAll() {
    for (const key in this.sensTableRowsSelected) {
      if (this.sensTableRowsSelected.hasOwnProperty(key)) {
        this.sensTableRowsSelected[key] = this.allRowsSelected;
      }
    }
    this.selectionChanged();
  }

  /**
   * applies percent change and interval to all selected inputs and updates the table
   */
  applyToAll() {
    this.pageIsDirty = true;
    let inputsValid = true, valid;
    valid = this.checkInput(this.changePercent, true);
    inputsValid = inputsValid && valid;
    if (!inputsValid) {
      return;
    }
    let callCreateTableData = false;

    for (const input of this.selectedInputData) {
      if (
        this.selectedCount === 0
        || this.selectedCount > 0 && this.sensTableRowsSelected[input.id]
      ) {
        if (this.changePercent.value) {
          if (input.value != null) {
            input.min = input.value * (100 - this.changePercent.value) * 0.01;
            input.max = input.value * (100 + this.changePercent.value) * 0.01;
            callCreateTableData = true;
          }
        }
      }
    }

    if (callCreateTableData) {
      this.createTableData();
    }

    setTimeout(() => {
      for (const tableItem of this.sensTableData) {
        this.checkInputs(tableItem);
      }
    });
  }

  /**
   * resets the min, max, and interval of data in the table
   */
  resetValues() {
    this.pageIsDirty = true;
    for (const input of this.inputsDropdownData) {
      if (
        this.selectedCount === 0
        || this.selectedCount > 0 && this.sensTableRowsSelected[input.id]
      ) {
        input.max = null;
        input.min = null;
      }
    }
    this.displayResetPopup = false;
    this.selectedCount = 0;
    this.sensTableRowsSelected = {};
    this.allRowsSelected = false;
    this.createTableData();
  }

  onSubmit() {
    this.errorsToCheck['empty-input'] = true;
    this.errorsToCheck['no-inputs'] = true;
    this.displaySavePopup = this.validateForm();
  }

  validateForm() {
    let isValid = true, inputIsValid = true;
    // table inputs check
    for (const tableItem of this.sensTableData) {
      if (tableItem.id) {
        inputIsValid = this.checkInputs(tableItem);
        isValid = isValid && inputIsValid;
      }
    }
    inputIsValid = this.summariseErrors();
    isValid = isValid && inputIsValid;
    return isValid;
  }

  onSaveCancel() {
    this.displaySavePopup = false;
  }

  /**
   * checks if the table data is valid, then saves the sensitivity to the database
   */
  onSaveSensitivity() {
    const newSens = new Sensitivity();
    for (const tableItem of this.sensTableData) {
      if (tableItem.id) {
        const newItem = new SensitivityItem();
        tableItem.id = ResultsUtils.processNameElements(this.translationService, tableItem.nameElements);
        newItem.setBasicInfo(tableItem.id, tableItem.nameElements, tableItem.value,
          tableItem.unit, true, tableItem.path);
        const selectedItem = this.selectedInputData.find(i => i.id === tableItem.id);
        // Use the raw value if available
        newItem.max = (selectedItem) ? selectedItem.max : tableItem.max;
        newItem.min = (selectedItem) ? selectedItem.min : tableItem.min;
        newItem.category = tableItem.category;
        newSens.inputs.push(newItem);
      }
    }
    this.projCommService.saveSensitivity(newSens);
    this.pageIsDirty = false;
    this.displaySavePopup = false;
  }

  selectionChanged() {
    this.selectedCount = 0;
    for (const key in this.sensTableRowsSelected) {
      if (this.sensTableRowsSelected[key]) {
        this.selectedCount++;
      }
    }
    this.allRowsSelected = this.selectedCount === Object.keys(this.sensTableRowsSelected).length;
  }

  canDeactivate(): Observable<boolean> | boolean {
    if ((this.pageIsDirty)) {
      return this.dialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRMATION'));
    }
    return true;
  }

  checkIfNum(value: any) {
    return Utils.isNumeric(value);
  }

  onInputChange(tableItem: InputSelectionTableItem, replace?: 'min' | 'max') {
    if (replace) {
      // Update the value stored in the selected input data when the user changes the value manually
      const input = this.selectedInputData.find(i => ResultsUtils.processNameElements(this.translationService, i.nameElements) === ResultsUtils.processNameElements(this.translationService, tableItem.nameElements));
      input[replace] = tableItem[replace];
    }
    this.checkInputs(tableItem);
    this.summariseErrors();
  }

  checkInputs(tableItem: InputSelectionTableItem) {
    tableItem.minErrorHints = [];
    tableItem.maxErrorHints = [];
    this.checkNaNInputs(tableItem);
    this.checkMinMax(tableItem);
    this.checkNegativeInputs(tableItem);
    this.checkPercentInputs(tableItem);
    if (this.errorsToCheck['empty-input']) {
      this.checkEmptyInputs(tableItem);
    }
    this.updateInputsValidity(tableItem);
    return (tableItem.minErrorHints.length === 0 &&
      tableItem.maxErrorHints.length === 0);
  }

  checkMinMax(tableItem: InputSelectionTableItem) {
    if ((tableItem.min || tableItem.min === 0) && (tableItem.max || tableItem.max === 0)) {
      if (tableItem.min >= tableItem.max) {
        if (!tableItem.minErrorHints) {
          tableItem.minErrorHints = [];
        }
        tableItem.minErrorHints.push('inconsistent-min-max');
        tableItem.maxErrorHints.push('inconsistent-min-max');
        if (this.temperror === '') {
          this.projCommService.postFromUnitValue('inconsistent-min-max-ADA-Sensitivity', Date.now());
          this.temperror = 'inconsistent-min-max-ADA-Sensitivity';
        }
      }
    }
  }

  checkEmptyInputs(tableItem: InputSelectionTableItem) {
    if (!(tableItem.min || tableItem.min === 0)) {
      tableItem.minErrorHints.push('empty-input');
      this.projCommService.postFromUnitValue('min-is-empty-ADA-Sensitivity', Date.now());
    }
    if (!(tableItem.max || tableItem.max === 0)) {
      tableItem.maxErrorHints.push('empty-input');
      this.projCommService.postFromUnitValue('max-is-empty-ADA-Sensitivity', Date.now());
    }
  }

  checkPercentInputs(tableItem: InputSelectionTableItem) {
    if (tableItem.unit !== '%') {
      return;
    }

    if (tableItem.min && tableItem.min > 100) {
      tableItem.minErrorHints.push('percent-over-100');
      this.projCommService.postFromUnitValue('min-percent-over-100-ADA-Sensitivity', Date.now());
    }
    if (tableItem.max && tableItem.max > 100) {
      tableItem.maxErrorHints.push('percent-over-100');
      this.projCommService.postFromUnitValue('max-percent-over-100-ADA-Sensitivity', Date.now());
    }
  }

  checkNegativeInputs(tableItem: InputSelectionTableItem) {
    if (tableItem.min && tableItem.min < 0) {
      tableItem.minErrorHints.push('negative-input');
      this.projCommService.postFromUnitValue('min-is-Negative-ADA-Sensitivity', Date.now());
    }
    if (tableItem.max && tableItem.max < 0) {
      tableItem.maxErrorHints.push('negative-input');
      this.projCommService.postFromUnitValue('max-is-Negative-ADA-Sensitivity', Date.now());
    }
  }

  checkNaNInputs(tableItem: InputSelectionTableItem) {
    if ((tableItem.min || tableItem.min === 0) && Number.isNaN(tableItem.min)) {
      tableItem.minErrorHints.push('is-NaN');
      this.projCommService.postFromUnitValue('min-is-NaN-ADA-Sensitivity', Date.now());

    }
    if ((tableItem.max || tableItem.max === 0) && Number.isNaN(tableItem.max)) {
      tableItem.maxErrorHints.push('is-NaN');
      this.projCommService.postFromUnitValue('max-is-NaN-ADA-Sensitivity', Date.now());
    }
  }

  updateInputsValidity(tableItem: InputSelectionTableItem) {
    if (this.sensForm) {
      const minInput: UntypedFormControl = this.sensForm.form.controls[tableItem.id + '-min'] as UntypedFormControl;
      if (minInput) {
        if (tableItem.minErrorHints.length === 0) {
          minInput.setErrors(null);
        } else {
          minInput.setErrors({'incorrect-min': true});
        }
        minInput.markAsTouched();
      }
      const maxInput: UntypedFormControl = this.sensForm.form.controls[tableItem.id + '-max'] as UntypedFormControl;
      if (maxInput) {
        if (tableItem.maxErrorHints.length === 0) {
          maxInput.setErrors(null);
        } else {
          maxInput.setErrors({'incorrect-max': true});
        }
        maxInput.markAsTouched();
      }
    }
  }

  addFormError(errName: string) {
    const index = this.formErrors.indexOf(errName);
    if (index === -1) {
      this.formErrors.push(errName);
    }
  }

  removeFormError(errName: string) {
    let index = this.formErrors.indexOf(errName);
    while (index > -1) {
      this.formErrors.splice(index, 1);
      index = this.formErrors.indexOf(errName);
    }
  }

  summariseErrors() {
    this.formErrors = [];
    let isValid = true;
    // no inputs check
    if (this.selectedInputData.length === 0) {
      this.addFormError('no-inputs');
      isValid = false;
    } else {
      this.removeFormError('no-inputs');
    }
    // table inputs check
    for (const tableItem of this.sensTableData) {
      if (tableItem.id) {
        const errorsArrs = [tableItem.minErrorHints, tableItem.maxErrorHints];
        for (const errors of errorsArrs) {
          for (const error of errors) {
            this.addFormError(error);
            isValid = false;
          }
        }
      }
    }
    return isValid;
  }

  checkInput(input: { name: string, value: number | null, errors: string[] }, emptyCheck = false) {
    if (!this.sensForm) return;
    input.errors = [];
    // check input validness
    if (input.value || input.value === 0) {
      if (input.name === 'changePercent') {
        if (input.value > 100) {
          input.errors.push('percent-over-100');
          this.projCommService.postFromUnitValue('percent-over-100-ADA-Sensitivity', Date.now());

        }
        if (input.value <= 0) {
          input.errors.push('non-positive');
          this.projCommService.postFromUnitValue('non-positive-ADA-Sensitivity', Date.now());
        }
      }
    } else if (emptyCheck && input.value !== 0) {
      input.errors.push('empty-input');
      this.projCommService.postFromUnitValue('empty-input-ADA-Sensitivity', Date.now());

    }
    // update control
    const inputControl: UntypedFormControl = this.sensForm.form.controls[input.name] as UntypedFormControl;
    if (inputControl) {
      if (input.errors.length === 0) {
        inputControl.setErrors(null);
      } else {
        inputControl.setErrors({'incorrect': true});
      }
      inputControl.markAsTouched();
    }
    return input.errors.length === 0;
  }

  getDisabledState(curInput: SensitivityItem) {
    if (this.selectedInputData && this.selectedInputData.length > 9) {
      for (const input of this.selectedInputData) {
        if (ResultsUtils.processNameElements(this.translationService, curInput.nameElements) === ResultsUtils.processNameElements(this.translationService, input.nameElements)) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  /**
   * gets all inputs from the current project
   */
  private getProjectInputs() {
    this.inputsDropdownData = [];
    const materialInputs = this.projectService.getNewFeedstockInputs(true);
    this.populateInputDataArray(materialInputs, 'materials');
    const plantSpecInputs = this.projectService.getNewPlantSpecInputs(true);
    this.populateInputDataArray(plantSpecInputs, 'plant specifications');
    if (this.projectService.project.projectInfo.useFinancial) {
      const financialInputs = this.projectService.getNewFinancialInputs(true);
      this.populateInputDataArray(financialInputs, 'financial');
    }
    // remove curve coefficient from sensitivity input selection
    this.filteredInputsData  = this.inputsDropdownData.filter(function(input) {
      const inputId = input.id.split(',');
      return inputId[inputId.length - 1].trim() !== 'curve coefficient';
    });
    this.updateSelectedInputDropdown();
  }

  /**
   * populates the inputs data object with project inputs
   * @param projectInputs - an array of project inputs
   * @param key - specifies which category the inputs are part of
   */
  private populateInputDataArray(projectInputs: ProjectInputItem[], key: string) {
    for (const input of projectInputs) {
      if (input) {
        this.inputTranslations[input.id] = ResultsUtils.processNameElements(this.translationService, input.nameElements);
        const sensInputs = SensitivityItem.fromProjectInput(input);
        sensInputs.category = key;
        this.inputsDropdownData.push(sensInputs);
      }
    }

  }

  /**
   * populates the table data object
   */
  private createTableData() {
    this.sensTableData = [];
    for (const item of this.selectedInputData) {
      this.sensTableData.push(new InputSelectionTableItem({
        id: item.id,
        nameElements: item.nameElements,
        min: (item.min != null) ? Number(Utils.roundToPrecision(item.min, this.projCommService.getPrecision())) : item.min,
        max: (item.max != null) ? Number(Utils.roundToPrecision(item.max, this.projCommService.getPrecision())) : item.max,
        value: item.value,
        unit: item.unit,
        category: item.category,
        path: item.path,
      }));
      this.sensTableRowsSelected[item.id] = this.sensTableRowsSelected[item.id];
    }
    if (this.sensTableData.length === 0) {
      this.sensTableData.push(new InputSelectionTableItem());
    }
    this.showTable = true;
    this.validateForm();
  }

  /**
   * populates the selected inputs object and updates values
   */
  private updateSelectedInputDropdown() {
    const sensCopy = Utils.deepCopy(this.sensitivity);
    this.selectedInputData = [];
    if (sensCopy) {
      for (const input of sensCopy.inputs) {
        if (input.selected) {
          for (const i of this.inputsDropdownData) {
            if (ResultsUtils.processNameElements(this.translationService, i.nameElements) === ResultsUtils.processNameElements(this.translationService, input.nameElements)) {
              i.selected = true;
              if (i.value === input.value) {
                i.min = input.min;
                i.max = input.max;
              }
              this.selectedInputData.push(i);
            }
          }
        }
      }
    }
  }

  private updateInputTranslations() {
    for (const input of this.inputsDropdownData) {
      if (input) {
        this.inputTranslations[input.id] = ResultsUtils.processNameElements(this.translationService, input.nameElements);
      }
    }
  }

  processUnitName(translationId: string) {
    return Unit.parseUnitDisplayName(this.translationService.get(translationId));
  }
}
